import type { Dispatch, SetStateAction } from 'react';
import { LuPlus, LuTrash2 } from 'react-icons/lu';

export type ContentEntry = { label: string; content: string };

/** 同じラベルがあれば上書き、なければ末尾に追加 */
export function mergeContentEntries(
  existing: ContentEntry[],
  incoming: ContentEntry[]
): ContentEntry[] {
  const result = [...existing];
  for (const entry of incoming) {
    const idx = result.findIndex((e) => e.label === entry.label && e.label !== '');
    if (idx >= 0) {
      result[idx] = entry;
    } else {
      result.push(entry);
    }
  }
  return result;
}

type AdminContentEntryListProps = {
  /** セクションのタイトル（例: 「求人情報」「エージェント向け項目」） */
  title: string;
  /** エントリ一覧 */
  entries: ContentEntry[];
  /** エントリ一覧の更新関数 */
  onChange: Dispatch<SetStateAction<ContentEntry[]>>;
  /** key のプレフィックス（例: 'job' | 'agent'） */
  keyPrefix: string;
};

/** ラベル + コンテンツの動的リスト（追加・編集・削除） */
export default function AdminContentEntryList({
  title,
  entries,
  onChange,
  keyPrefix,
}: AdminContentEntryListProps) {
  const handleAdd = () => {
    onChange((prev) => [...prev, { label: '', content: '' }]);
  };

  const handleLabelChange = (index: number, value: string) => {
    onChange((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], label: value };
      return next;
    });
  };

  const handleContentChange = (index: number, value: string) => {
    onChange((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], content: value };
      return next;
    });
  };

  const handleRemove = (index: number) => {
    onChange((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="min-w-0">
      <div className="mb-1 flex items-center justify-between">
        <span className="font-medium text-main">{title}</span>
        <button
          type="button"
          onClick={handleAdd}
          className="inline-flex items-center gap-1 rounded border-0 bg-transparent px-2 py-1 text-xs text-primary hover:bg-gray-100"
        >
          <LuPlus className="size-3.5" />
          項目を追加
        </button>
      </div>
      <div className="flex flex-col gap-2">
        {entries.map((entry, index) => (
          <div
            key={`${keyPrefix}-${index}`}
            className="flex min-w-0 gap-2 rounded-lg bg-gray-50 p-3"
          >
            <div className="min-w-0 flex-1 space-y-2">
              <input
                type="text"
                value={entry.label}
                onChange={(e) => handleLabelChange(index, e.target.value)}
                placeholder="ラベル"
                className="box-border w-full max-w-full min-w-0 rounded border border-solid border-border bg-white px-2 py-1.5 text-sm text-main"
              />
              <textarea
                value={entry.content}
                onChange={(e) => handleContentChange(index, e.target.value)}
                placeholder="コンテンツ"
                rows={3}
                className="box-border min-h-[72px] w-full max-w-full min-w-0 resize-y rounded border border-solid border-border bg-white px-2 py-1.5 text-sm text-main"
              />
            </div>
            <button
              type="button"
              onClick={() => handleRemove(index)}
              className="shrink-0 self-start rounded border-0 bg-transparent p-1 text-sub hover:bg-gray-100 hover:text-main"
              aria-label="この項目を削除"
            >
              <LuTrash2 className="size-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
